import { config } from 'config';
import { createAsyncThunk } from 'app/store/utils';
import axios from 'axios';
import { Link } from 'types/api.types';

import {
  CheckoutSubscriptionResponse,
  CheckSubscriptionResponse,
  FetchTiersListResponse,
  ManageSubscriptionResponse,
  SubscriptionQuotasResponse,
  SubscriptionTiersDDL,
} from 'types/subscription.types';
import { ApiErrorCodeTypes } from 'types/enums';
import { camelCase } from 'lodash';
import { addAlertAction } from '../Alerts/slice';
import {
  getSignedCheckoutLink,
  getCheckSubscriptionLink,
  getSubscriptionQuotasLink,
  getSubscriptionProvider,
  isCoWriterLimitExceeded,
} from './selectors';
import { setShowUpgradePopup } from '../Storage/slice';

export const fetchSubscriptionTiersDDLEffect = createAsyncThunk(
  'subscription/fetchSubscriptionTiersDDL',
  async (data: { link?: Link }) => {
    if (!data.link) {
      throw new Error('Subscription Tiers DDL link not found');
    }

    const response = await axios.request<SubscriptionTiersDDL>({
      url: data.link?.href,
      method: data.link?.method,
    });

    return response.data;
  }
);

export const fetchSubscriptionTierListEffect = createAsyncThunk(
  'subscription/fetchSubscriptionTierList',
  async (data: { link?: Link }, { dispatch }) => {
    if (!data.link) {
      throw new Error('Fetch subscription tiers link not found');
    }

    try {
      const response = await axios.request<FetchTiersListResponse>({
        url: data.link.href,
        method: data.link.method,
        withCredentials: true,
      });

      return response.data.response;
    } catch (e) {
      dispatch(
        addAlertAction({
          message:
            'Failed to get subscription plans. Please refresh the page and try again',
        })
      );
      throw e;
    }
  }
);

export const checkoutSubscriptionEffect = createAsyncThunk(
  'subscription/checkoutSubscription',
  async (
    data: { priceId: string; currency: string },
    { dispatch, getState }
  ) => {
    try {
      const link = getSignedCheckoutLink(getState());

      if (!link) {
        throw new Error('Checkout signed checkout link not found');
      }

      const response = await axios.request<CheckoutSubscriptionResponse>({
        url: link.href.replace('{price.id}', data.priceId),
        params: { currency: data.currency },
        method: link.method,
        withCredentials: true,
      });

      return response.data;
    } catch (e) {
      dispatch(addAlertAction({ message: 'Failed to create subscription' }));
      throw e;
    }
  }
);

export const checkSubscriptionEffect = createAsyncThunk(
  'subscription/checkSubscription',
  async (_data, { dispatch, getState }) => {
    const state = getState();
    const link = getCheckSubscriptionLink(state);

    if (!link) {
      throw new Error('Check Subscription link not found');
    }

    try {
      const response = await axios.request<CheckSubscriptionResponse>({
        url: link.href,
        method: link.method,
        withCredentials: true,
      });

      return response.data.response.subscription.item;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to fetch subscription status' })
      );
      throw e;
    }
  }
);

export const manageSubscriptionEffect = createAsyncThunk(
  'subscription/manageSubscription',
  async (data: { link?: Link }, { dispatch, getState }) => {
    try {
      const provider = getSubscriptionProvider(getState());

      if (provider === 'Apple') {
        return {
          response: {
            _links: { portal: { href: 'https://www.apple.com/uk/app-store/' } },
          },
        } as ManageSubscriptionResponse;
      }

      if (!data.link) {
        throw new Error('Manage account link not found');
      }

      const response = await axios.request<ManageSubscriptionResponse>({
        url: data.link?.href,
        method: data.link?.method,
        withCredentials: true,
      });

      return response.data;
    } catch (e) {
      dispatch(
        addAlertAction({
          message:
            'Failed to get manage subscription link. Please try again later',
        })
      );
      throw e;
    }
  }
);

export const fetchSubscriptionQuotasEffect = createAsyncThunk(
  'subscription/fetchSubscriptionQuotas',
  async (_, { dispatch, getState }) => {
    const link = getSubscriptionQuotasLink(getState());
    try {
      if (!link) {
        throw new Error('Subscription quotas link not found');
      }

      const response = await axios.request<SubscriptionQuotasResponse>({
        url: link?.href,
        method: link?.method,
        withCredentials: true,
      });

      return response.data.response;
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to get subscription quotas. Please try again later',
        })
      );
      throw e;
    }
  }
);

export const fetchLyricsInspirationSearchEffect = createAsyncThunk(
  'subscription/fetchLyricsCowriter',
  async (
    {
      word,
      useLargeModel,
      type,
      lyrics,
    }: { word: string; useLargeModel: boolean; type: string; lyrics?: string },
    { dispatch, getState }
  ) => {
    const isLimitExceeded = isCoWriterLimitExceeded(getState());
    if (isLimitExceeded) {
      dispatch(
        setShowUpgradePopup({
          messageType: 'cowriter',
          context: { action: 'openCowriter' },
        })
      );
      throw new Error();
    }

    try {
      const url = `${config.sessionApiBaseUrl}/lyrics-cowriter/${type}`;
      const response = await axios.request<SubscriptionQuotasResponse>({
        url,
        method: 'POST',
        data: {
          word,
          useLargeModel,
          context: lyrics || '',
        },
        withCredentials: true,
      });

      const typeCamelCased = camelCase(type);
      return response.data.response[typeCamelCased];
    } catch (e: any) {
      if (
        e?.response?.data?.error?.errorCode === ApiErrorCodeTypes.LIMIT_EXCEEDED
      ) {
        dispatch(
          setShowUpgradePopup({
            messageType: 'cowriter',
            context: { action: 'openCowriter' },
          })
        );
      } else {
        dispatch(
          addAlertAction({
            message: `Failed to get ${type}. Please try again later`,
          })
        );
      }
      return [];
    }
  }
);
